import React, { useState, useRef, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    TextField,
    Button,
    Paper,
    Autocomplete,
    Grid,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    InputAdornment
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { useReactToPrint } from 'react-to-print';
import { getFornecedores, getInvestidores, getObras } from '../services/api';
import { Fornecedor, Investidor, Obra } from '../services/types';
import ReciboPrint, { Pagador } from '../components/ReciboPrint';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

const RecibosServicoPage: React.FC = () => {
    const [fornecedores, setFornecedores] = useState<Fornecedor[]>([]);
    const [investidores, setInvestidores] = useState<Investidor[]>([]);
    const [obras, setObras] = useState<Obra[]>([]);

    // Form State
    const [selectedFornecedor, setSelectedFornecedor] = useState<Fornecedor | null>(null);
    const [selectedInvestidor, setSelectedInvestidor] = useState<Investidor | null>(null);
    const [selectedObra, setSelectedObra] = useState<Obra | null>(null);
    const [valor, setValor] = useState<string>('');
    const [data, setData] = useState<string>(dayjs().format('YYYY-MM-DD'));
    const [tipoServico, setTipoServico] = useState<string | null>(null);
    const [descricaoManual, setDescricaoManual] = useState<string>('');
    const [cidade, setCidade] = useState<string>('Rio das Ostras');
    const [estado, setEstado] = useState<string>('RJ');
    const [tipoDocumento, setTipoDocumento] = useState<'CPF' | 'CNPJ'>('CPF');

    // Pagador Interveniente State
    const [pagadorInterveniente, setPagadorInterveniente] = useState<boolean>(false);
    const [selectedPagador, setSelectedPagador] = useState<Pagador | null>(null);

    const [quitaIntegralmente, setQuitaIntegralmente] = useState<boolean>(true);

    const servicosOpcoes = ['Pedreiro', 'Hidráulica', 'Pintura', 'Eletricista', 'Empreiteiro', 'Outros'];

    // Computed Pagadores Possiveis
    // Helper to filter service providers (or those with undefined type, defaulting to service)
    const isFornecedorServico = (f: Fornecedor) => !f.tipo || f.tipo === 'SERVICO' || f.tipo === 'AMBOS';

    const fornecedoresServico = fornecedores.filter(isFornecedorServico);

    const pagadoresPossiveis: Pagador[] = [
        ...investidores
            .filter(i => !selectedInvestidor || i.id !== selectedInvestidor.id) // Exclude selected Contratante
            .map(i => ({ id: i.id, nome: i.nome, documento: i.cpf_cnpj, tipo: 'Investidor' as const })),
        ...fornecedoresServico.map(f => ({ id: f.id, nome: f.nome_razao_social, documento: f.cnpj_cpf, tipo: 'Fornecedor' as const }))
    ];



    // Computed description
    const descricaoFinal = tipoServico === 'Outros' ? descricaoManual : (tipoServico || '');

    const componentRef = useRef<HTMLDivElement>(null);

    const handlePrint = useReactToPrint({
        contentRef: componentRef,
        documentTitle: `Recibo_${selectedFornecedor?.nome_razao_social || 'Servico'}_${data}`,
    });

    useEffect(() => {
        const loadData = async () => {
            try {
                const [fornecedoresData, investidoresData, obrasData] = await Promise.all([
                    getFornecedores(),
                    getInvestidores(),
                    getObras()
                ]);

                setFornecedores(fornecedoresData || []);
                setInvestidores(investidoresData || []);
                setObras(obrasData || []);
            } catch (error) {
                console.error("Erro ao carregar dados", error);
            }
        };
        loadData();
    }, []);

    // Auto-detect CPF/CNPJ based on length
    useEffect(() => {
        if (selectedFornecedor) {
            const doc = selectedFornecedor.cnpj_cpf.replace(/\D/g, '');
            if (doc.length > 11) {
                setTipoDocumento('CNPJ');
            } else {
                setTipoDocumento('CPF');
            }
        }
    }, [selectedFornecedor]);

    const handleValorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        // Basic currency input handling could be improved, but simple text is fine for now
        setValor(value);
    };

    const isFormValid = selectedFornecedor && selectedInvestidor && selectedObra && valor && descricaoFinal;

    return (
        <Container maxWidth="md">
            <Box className="no-print" sx={{ my: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom color="primary.main" sx={{ fontWeight: 'bold' }}>
                    Gerador de Recibos
                </Typography>

                <Paper elevation={3} sx={{ p: 4 }}>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Autocomplete
                                options={investidores}
                                getOptionLabel={(option) => option.nome}
                                value={selectedInvestidor}
                                onChange={(_, newValue) => setSelectedInvestidor(newValue)}
                                renderInput={(params) => <TextField {...params} label="Contratante (Investidor)" required />}
                                renderOption={(props, option) => {
                                    const { key, ...otherProps } = props;
                                    return (
                                        <li key={option.id} {...otherProps}>
                                            {option.nome}
                                        </li>
                                    );
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Autocomplete
                                options={fornecedoresServico}
                                getOptionLabel={(option) => option.nome_razao_social}
                                value={selectedFornecedor}
                                onChange={(_, newValue) => setSelectedFornecedor(newValue)}
                                renderInput={(params) => <TextField {...params} label="Beneficiário (Fornecedor)" required />}
                                renderOption={(props, option) => {
                                    const { key, ...otherProps } = props;
                                    return (
                                        <li key={option.id} {...otherProps}>
                                            {option.nome_razao_social}
                                        </li>
                                    );
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl component="fieldset">
                                <FormLabel component="legend">Documento do Beneficiário</FormLabel>
                                <RadioGroup
                                    row
                                    value={tipoDocumento}
                                    onChange={(e) => setTipoDocumento(e.target.value as 'CPF' | 'CNPJ')}
                                >
                                    <FormControlLabel value="CPF" control={<Radio />} label="CPF" />
                                    <FormControlLabel value="CNPJ" control={<Radio />} label="CNPJ" />
                                </RadioGroup>
                            </FormControl>
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <FormControl component="fieldset">
                                <FormLabel component="legend">Pagador Interveniente?</FormLabel>
                                <RadioGroup
                                    row
                                    value={pagadorInterveniente ? 'Sim' : 'Não'}
                                    onChange={(e) => {
                                        const isSim = e.target.value === 'Sim';
                                        setPagadorInterveniente(isSim);
                                        if (!isSim) setSelectedPagador(null);
                                    }}
                                >
                                    <FormControlLabel value="Não" control={<Radio />} label="Não (Padrão)" />
                                    <FormControlLabel value="Sim" control={<Radio />} label="Sim" />
                                </RadioGroup>
                            </FormControl>
                        </Grid>

                        {pagadorInterveniente && (
                            <Grid size={{ xs: 12 }}>
                                <Autocomplete
                                    options={pagadoresPossiveis}
                                    getOptionLabel={(option) => `${option.nome} (${option.tipo})`}
                                    value={selectedPagador}
                                    onChange={(_, newValue) => setSelectedPagador(newValue)}
                                    renderInput={(params) => <TextField {...params} label="Nome do Pagador" required />}
                                    renderOption={(props, option) => {
                                        const { key, ...otherProps } = props;
                                        return (
                                            <li key={`${option.tipo}-${option.id}`} {...otherProps}>
                                                {option.nome} ({option.tipo})
                                            </li>
                                        );
                                    }}
                                />
                            </Grid>
                        )}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Autocomplete
                                options={obras}
                                getOptionLabel={(option) => option.nome}
                                value={selectedObra}
                                onChange={(_, newValue) => setSelectedObra(newValue)}
                                renderInput={(params) => <TextField {...params} label="Referente a (Obra)" required />}
                                renderOption={(props, option) => {
                                    const { key, ...otherProps } = props;
                                    return (
                                        <li key={option.id} {...otherProps}>
                                            {option.nome}
                                        </li>
                                    );
                                }}
                            />
                        </Grid>
                        {/* Unidade selection removed as per request */}
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                label="Valor (R$)"
                                value={valor}
                                onChange={handleValorChange}
                                fullWidth
                                required
                                type="number"
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                label="Data"
                                type="date"
                                value={data}
                                onChange={(e) => setData(e.target.value)}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ max: "9999-12-31" }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    label="Cidade"
                                    value={cidade}
                                    onChange={(e) => setCidade(e.target.value)}
                                    fullWidth
                                />
                                <TextField
                                    label="Estado"
                                    value={estado}
                                    onChange={(e) => setEstado(e.target.value)}
                                    sx={{ width: '80px' }}
                                />
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <FormControl component="fieldset">
                                <FormLabel component="legend">O Pagamento quita integralmente o valor acordado?</FormLabel>
                                <RadioGroup
                                    row
                                    value={quitaIntegralmente ? 'Sim' : 'Não'}
                                    onChange={(e) => setQuitaIntegralmente(e.target.value === 'Sim')}
                                >
                                    <FormControlLabel value="Sim" control={<Radio />} label="Sim" />
                                    <FormControlLabel value="Não" control={<Radio />} label="Não" />
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Autocomplete
                                options={servicosOpcoes}
                                value={tipoServico}
                                onChange={(_, newValue) => {
                                    setTipoServico(newValue);
                                    if (newValue !== 'Outros') {
                                        setDescricaoManual('');
                                    }
                                }}
                                renderInput={(params) => <TextField {...params} label="Descrição do Serviço" required />}
                            />
                        </Grid>
                        {tipoServico === 'Outros' && (
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    label="Descreva o Serviço"
                                    value={descricaoManual}
                                    onChange={(e) => setDescricaoManual(e.target.value)}
                                    fullWidth
                                    multiline
                                    rows={2}
                                    required
                                    placeholder="Digite a descrição detalhada do serviço..."
                                />
                            </Grid>
                        )}

                        <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<PrintIcon />}
                                onClick={handlePrint}
                                disabled={!isFormValid}
                            >
                                Imprimir Recibo
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>

            </Box>

            {/* Hidden Print Component */}
            <div className="print-only">
                {selectedFornecedor && selectedInvestidor && (
                    <ReciboPrint
                        ref={componentRef}
                        fornecedor={selectedFornecedor}
                        investidor={selectedInvestidor}
                        unidade={null}
                        obra={selectedObra}
                        valor={parseFloat(valor) || 0}
                        data={new Date(data + 'T12:00:00')}
                        descricao={descricaoFinal}
                        cidade={cidade}
                        estado={estado}
                        tipoDocumentoBeneficiario={tipoDocumento}
                        pagador={selectedPagador}
                        quitaIntegralmente={quitaIntegralmente}
                    />
                )}
            </div>
        </Container >
    );
};

export default RecibosServicoPage;
